/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, { useState } from 'react';
import { Badge, Box, Button, Checkbox, Flex, Text } from '@radix-ui/themes';
import { ChevronDown, ChevronUp } from 'lucide-react';

import type { FilterSection } from '../../../../shared/FilterSidebar';
import { normalizeSeverity, SEVERITY_COLORS } from './SeverityBadge';
import { QUARANTINE_STRINGS as STRINGS } from './quarantine.strings';

const VISIBLE_OPTIONS_COUNT = 7;

export function QuarantineFilterGroup({
  section,
  onChange,
  disabled,
}: {
  section: FilterSection;
  onChange: (values: string[]) => void;
  disabled?: boolean;
}): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const options = section.options ?? [];
  const selected = Array.isArray(section.value) ? section.value : [];
  const visible = showAll ? options : options.slice(0, VISIBLE_OPTIONS_COUNT);
  const headingId = `quarantine-filter-${section.id}`;

  const toggle = (value: string, checked: boolean) =>
    onChange(checked ? [...selected, value] : selected.filter((v) => v !== value));

  return (
    <Box mt="1" role="group" aria-labelledby={headingId}>
      <Flex align="baseline" justify="between" wrap="wrap" gap="2" mb="3">
        <Text id={headingId} size="2" weight="bold">
          {section.label}
        </Text>
      </Flex>
      <Flex direction="column" gap="1">
        {visible.map((option) => (
          <Text key={option.value} as="label" size="2" className="quarantine-filters__option">
            <Checkbox
              checked={selected.includes(option.value)}
              onCheckedChange={(checked) => toggle(option.value, checked === true)}
              disabled={disabled}
              aria-label={option.label}
            />
            {section.id === 'severity' ? (
              <Flex align="center" gap="2">
                <Box
                  aria-hidden="true"
                  className="quarantine-filters__dot"
                  style={{ backgroundColor: `var(--${SEVERITY_COLORS[normalizeSeverity(option.value)]}-9)` }}
                />
                <Text size="2">{option.label}</Text>
              </Flex>
            ) : (
              <Text size="2">{option.label}</Text>
            )}
            {option.count !== undefined && (
              <Badge size="1" color="gray" variant="soft" radius="full">
                {option.count}
              </Badge>
            )}
          </Text>
        ))}
        {options.length > VISIBLE_OPTIONS_COUNT && (
          <Button
            variant="ghost"
            color="blue"
            size="1"
            onClick={() => setShowAll(!showAll)}
            aria-expanded={showAll}
            className="quarantine-filters__see-more"
          >
            {showAll ? <ChevronUp size={12} aria-hidden="true" /> : <ChevronDown size={12} aria-hidden="true" />}
            <Text size="2">{showAll ? STRINGS.filters.seeLess : STRINGS.filters.seeMore}</Text>
          </Button>
        )}
      </Flex>
    </Box>
  );
}
